import {
  RequirementWorkflowType,
  type WorkerRequirementStatus,
} from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";

import { type WorkerRequirement } from "../Documents/api/types";
import { isAutoShiftUnassignmentWithinValidityWindow } from "../Documents/Details/Workflows/Checkr/isAutoShiftUnassignmentWithinValidityWindow";

function isStatusIncluded(params: {
  filterStatus: WorkerRequirementStatus[];
  requirementStatus: WorkerRequirementStatus;
}): boolean {
  return params.filterStatus.includes(params.requirementStatus);
}

function isAutoUnassigned(params: {
  autoShiftUnassignmentAt?: string;
  autoShiftUnassignmentValidityWindowInDays?: number;
}): boolean {
  const { autoShiftUnassignmentAt, autoShiftUnassignmentValidityWindowInDays } = params;
  if (!isDefined(autoShiftUnassignmentAt)) {
    return false;
  }

  return isAutoShiftUnassignmentWithinValidityWindow({
    autoShiftUnassignmentAt,
    autoShiftUnassignmentValidityWindowInDays,
  });
}

/**
 * This helper explains how background-check requirements should be shown in the app.
 *
 * We display requirements (or requirement-related UI) in several places, for example:
 * 1. The blocking-requirements carousel in the shift bottom sheet
 * 2. Missing-requirements pills on the shift card in shift discovery
 * 3. The requirements card in the Documents page (Accounts section)
 * 4. Requirement pills inside the Documents page, etc.
 *
 * Background checks behave differently from other requirements, and therefore follow
 * their own display rules.
 *
 * With passive background checks (i.e., checks triggered at booking rather than before),
 * background checks are no longer considered blocking requirements. In fact, we do not
 * want to show them as requirements at all. So UI components use this helper to filter
 * background-check items out of the requirements list.
 *
 * Example: If a background check is “MISSING,” workers targeted for passive checks are still
 * allowed to book shifts (as long as other requirements are met), and we intentionally hide
 * any background-check requirement from them. These components should therefore use this helper
 * to exclude background checks from what they display. Workers *not* targeted for passive checks
 * should still be blocked, and those components should *not* use this helper.
 *
 * Certain scenarios override this hiding behavior. For example, if a worker is auto-unassigned
 * because Checkr did not return results within X hours, we *do* want to inform them that their
 * background check is under review — even if they are part of the passive background-check flow.
 * In those cases, components use the `onlyIfNotAutoUnassigned` filter to allow background checks
 * to be shown.
 */
export function filterOutBackgroundCheckRequirements(params: {
  workerRequirements: WorkerRequirement[];
  autoShiftUnassignmentAt?: string;
  autoShiftUnassignmentValidityWindowInDays?: number;
  filter?: {
    status?: WorkerRequirementStatus[];
    onlyIfNotAutoUnassigned?: boolean;
  };
}): WorkerRequirement[] {
  const {
    workerRequirements,
    autoShiftUnassignmentAt,
    filter,
    autoShiftUnassignmentValidityWindowInDays,
  } = params;
  return workerRequirements.filter((workerRequirement) => {
    if (
      workerRequirement.requirement?.attributes.requirementWorkflowType !==
      RequirementWorkflowType.CHECKR_BACKGROUND_CHECK
    ) {
      return true;
    }

    if (filter?.onlyIfNotAutoUnassigned) {
      if (
        !isAutoUnassigned({ autoShiftUnassignmentAt, autoShiftUnassignmentValidityWindowInDays })
      ) {
        if (isDefined(filter?.status)) {
          return !isStatusIncluded({
            filterStatus: filter.status,
            requirementStatus: workerRequirement.status,
          });
        }

        return false;
      }

      return true;
    }

    if (isDefined(filter?.status)) {
      return !isStatusIncluded({
        filterStatus: filter.status,
        requirementStatus: workerRequirement.status,
      });
    }

    return false;
  });
}
